import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { v4 as uuidv4 } from 'uuid';
import { getBrowserAndOSInfo } from '../../helpers/browserHelpers';
import { logger } from '../../helpers/logger';

/**
 * Global instance of AppInsights ReactPlugin.
 */
window.AppInsightsReactPlugin = null;

/**
 * Returns the global instance of AppInsights. Only for use when a redux thunk
 * or similar "out-of-band" code needs to log to app insights. Use the `useAppInsightsContext`
 * hook to access the instance within a component.
 */
export const getAppInsightsInstance = () => window.AppInsightsReactPlugin;

/**
 * In-memory fallback tab id used when sessionStorage is unavailable.
 */
let inMemoryTabId = null;
let usedInMemoryTabIdFallback = false;
let tabIdFallbackErrorName = null;

/**
 * Gets or creates a unique tab identifier that persists across page refreshes.
 * @returns {string} - Unique tab identifier
 */
const getOrCreateTabId = () => {
    // sessionStorage can throw a SecurityError ("Access is denied for this document")
    // when storage access is blocked — e.g. embedded webviews opened from SMS/email
    // links or browsers blocking third-party storage. Fall back to an in-memory id so
    // AppInsights initialization (and therefore the whole app) never fails to load.
    try {
        let tabId = sessionStorage.getItem('appInsights_tabId');
        if (!tabId) {
            tabId = uuidv4();
            sessionStorage.setItem('appInsights_tabId', tabId);
        }
        return tabId;
    } catch (error) {
        usedInMemoryTabIdFallback = true;
        tabIdFallbackErrorName = error?.name || 'UnknownError';
        if (!inMemoryTabId) {
            inMemoryTabId = uuidv4();
        }
        return inMemoryTabId;
    }
};

/**
 * Configures telemetry initializers for the AppInsights instance.
 * @param {*} appInsights - ApplicationInsights instance
 * @param {*} reactPlugin - ReactPlugin instance
 * @param {string} portalName - Portal name for telemetry tagging
 * @param {string} tabId - Unique identifier for this browser tab/session
 */
const setupTelemetryInitializers = (appInsights, reactPlugin, portalName, tabId) => {
    // Get browser and OS information once for this session
    const { browser, os } = getBrowserAndOSInfo();

    appInsights.addTelemetryInitializer((envelope) => {
        envelope.data.portal = portalName;
        envelope.data.tabId = tabId;
        envelope.data.browser = browser;
        envelope.data.operatingSystem = os;

        if (reactPlugin.user) {
            envelope.data.userId = reactPlugin.user.userId;
            envelope.data.userTypeId = reactPlugin.user.userTypeId;
            envelope.data.userTypeName = reactPlugin.user.userType?.userTypeName;
            envelope.data.userName = reactPlugin.user.userName;
            envelope.data.firstName = reactPlugin.user.person?.firstName;
            envelope.data.lastName = reactPlugin.user.person?.lastName;
            envelope.data.originClientId = reactPlugin.user.originClientId;
        }

        if (portalName === 'patient' && reactPlugin.patientContext) {
            // Merge all patient context props so domains like facility/market are included
            Object.assign(envelope.data, reactPlugin.patientContext);
        }

        if (window) {
            envelope.data.viewportWidth = window.innerWidth;
            envelope.data.viewportHeight = window.innerHeight;
        }
        if (window?.screen) {
            envelope.data.screenWidth = window.screen.width;
            envelope.data.screenHeight = window.screen.height;
        }
    });

    appInsights.addTelemetryInitializer((envelope) => {
        // filter out noisy ResizeObserver errors
        const errorMessage =
            envelope.baseData?.exceptions?.length && envelope.baseData?.exceptions[0]?.message;

        if (errorMessage && errorMessage?.includes('ResizeObserver')) return false;

        return true;
    });
};

/**
 * Configures dependency listeners and initializers for tracking aborted requests and duplicate API calls.
 * @param {*} appInsights - ApplicationInsights instance
 */
const setupDependencyTracking = (appInsights) => {
    // Track recent API calls to detect duplicates within a time window
    const recentApiCalls = new Map();
    const DUPLICATE_DETECTION_WINDOW_MS = 500;

    // Expose for debugging in non-production
    if (process.env.NODE_ENV !== 'production') {
        window.recentApiCalls = recentApiCalls;
    }

    /**
     * Creates a unique key for an API call based on method, path, and payload
     */
    const createApiCallKey = (method, url, data) => {
        // Extract just the pathname (no query string or hash)
        let path = url;
        try {
            const urlObj = new URL(url);
            path = urlObj.pathname;
        } catch {
            // If URL parsing fails, use the full URL
        }

        const dataKey = data ? JSON.stringify(data) : '';
        return `${method}:${path}:${dataKey}`;
    };

    /**
     * Extracts the root endpoint (method + path) from a URL for reporting
     */
    const getRootEndpoint = (method, url) => {
        let path = url;
        try {
            const urlObj = new URL(url);
            path = urlObj.pathname;
        } catch {
            // If URL parsing fails, use the full URL
        }
        return `${method} ${path}`;
    };

    /**
     * Generates a stable hash for a list of IDs to create a duplicate group identifier
     */
    const hashIds = (ids) => {
        const str = ids.join(',');
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    };

    // Mark aborted/cancelled dependency (XHR/fetch) calls so they can be filtered in App Insights
    // Listener runs before the request; capture abort events and stash a flag in the shared context
    appInsights.addDependencyListener((details) => {
        // Ensure context exists
        details.context = details.context || {};

        // Detect duplicate API calls
        const now = Date.now();

        // Extract URL and method from different possible locations in the details object
        // The structure varies between XHR and fetch, and between when listener fires
        const method =
            details?.item?.method || details?.options?.method || details?.method || 'GET';
        const url = details?.item?.target || details?.item?.name || details?.input || '';
        const data = details?.init?.body || details?.options?.body;

        // Skip tracking if we don't have a valid URL (likely non-HTTP request like SignalR WebSocket)
        if (!url || typeof url !== 'string') {
            return;
        }

        const callKey = createApiCallKey(method, url, data);

        // Clean up old entries (older than the detection window)
        for (const [key, callData] of recentApiCalls.entries()) {
            if (now - callData.lastCallTime > DUPLICATE_DETECTION_WINDOW_MS) {
                recentApiCalls.delete(key);
            }
        }

        // Check if this call was made recently
        const existingCall = recentApiCalls.get(callKey);

        if (existingCall) {
            // Check if the previous call was aborted - if so, don't count as duplicate
            if (existingCall.wasAborted) {
                // Previous call was cancelled, treat this as a fresh call
                existingCall.count = 1;
                existingCall.lastCallTime = now;
                existingCall.wasAborted = false;
                existingCall.dependencyIds = details?.item?.id ? [details.item.id] : [];
                existingCall.rootEndpoint = getRootEndpoint(method, url);
            } else {
                // Duplicate detected!
                const timeSinceLastCall = now - existingCall.lastCallTime;
                existingCall.count++;
                existingCall.lastCallTime = now;
                existingCall.wasAborted = false;

                // Track dependency IDs for correlation (item.id may not be set yet at listener time)
                const itemId = details?.item?.id;
                if (itemId) {
                    existingCall.dependencyIds = existingCall.dependencyIds || [];
                    existingCall.dependencyIds.push(itemId);
                }

                // Generate stable group ID from all dependency IDs in this burst
                // If we don't have IDs yet, use a hash of the endpoint + timestamp
                const duplicateGroupId =
                    existingCall.dependencyIds.length > 0
                        ? hashIds(existingCall.dependencyIds)
                        : hashIds([callKey, now.toString()]);

                // Mark this dependency as a duplicate in its properties
                details.context.isDuplicate = true;
                details.context.duplicationCount = existingCall.count;
                details.context.timeSinceLastCallMs = timeSinceLastCall;
                details.context.dependencyIds = existingCall.dependencyIds;
                details.context.duplicateGroupId = duplicateGroupId;
                details.context.rootEndpoint = existingCall.rootEndpoint;

                // Log to console in non-production for debugging
                if (process.env.NODE_ENV !== 'production') {
                    logger.warn(
                        `⚠️ Duplicate API call detected (${existingCall.count}x): ${method} ${url}`,
                        {
                            timeSinceLastCallMs: `${timeSinceLastCall}ms`,
                            dependencyIds: existingCall.dependencyIds,
                            duplicateGroupId,
                            rootEndpoint: existingCall.rootEndpoint,
                            data,
                        },
                    );
                }
            }
        } else {
            // First call, register it
            recentApiCalls.set(callKey, {
                count: 1,
                lastCallTime: now,
                wasAborted: false,
                dependencyIds: details?.item?.id ? [details.item.id] : [],
                rootEndpoint: getRootEndpoint(method, url),
            });
        }

        // fetch(): watch AbortSignal
        const signal = details?.init?.signal;
        if (signal && typeof signal.addEventListener === 'function') {
            if (signal.aborted) {
                details.context.isAborted = true;
                details.context.abortReason = 'AbortError';

                // Mark as aborted in our duplicate tracking
                const callData = recentApiCalls.get(callKey);
                if (callData) {
                    callData.wasAborted = true;
                }
            } else {
                const onAbort = () => {
                    details.context.isAborted = true;
                    details.context.abortReason = 'AbortError';

                    // Mark as aborted in our duplicate tracking
                    const callData = recentApiCalls.get(callKey);
                    if (callData) {
                        callData.wasAborted = true;
                    }
                };
                try {
                    signal.addEventListener('abort', onAbort, { once: true });
                } catch (_) {
                    // no-op
                }
            }
        }

        // XHR: listen for abort event
        const xhr = details?.xhr;
        if (xhr && typeof xhr.addEventListener === 'function') {
            const onAbort = () => {
                details.context.isAborted = true;
                details.context.abortReason = 'AbortError';

                // Mark as aborted in our duplicate tracking
                const callData = recentApiCalls.get(callKey);
                if (callData) {
                    callData.wasAborted = true;
                }

                try {
                    xhr.removeEventListener('abort', onAbort);
                } catch (_) {
                    // Cleanup failure is non-critical
                }
            };
            try {
                xhr.addEventListener('abort', onAbort);
            } catch (_) {
                // Event listener attachment failure is non-critical
            }
        }
    });

    // Initializer runs after the request completes; copy the abort and duplicate flags to dependency telemetry
    appInsights.addDependencyInitializer((details) => {
        const wasAborted = !!details?.context?.isAborted;
        const isDuplicate = !!details?.context?.isDuplicate;

        if (details?.item) {
            details.item.properties = details.item.properties || {};

            // Add abort flags
            if (wasAborted) {
                details.item.properties.isAborted = 'true';
                details.item.properties.abortReason = details.context.abortReason || 'AbortError';
            }

            // Add duplicate detection flags
            if (isDuplicate) {
                details.item.properties.isDuplicate = 'true';
                details.item.properties.duplicationCount = details.context.duplicationCount;
                details.item.properties.timeSinceLastCallMs = details.context.timeSinceLastCallMs;
                details.item.properties.duplicateGroupId = details.context.duplicateGroupId;
                details.item.properties.rootEndpoint = details.context.rootEndpoint;

                // Store the list of related dependency IDs for correlation (ordered)
                if (details.context.dependencyIds) {
                    details.item.properties.relatedDependencyIds =
                        details.context.dependencyIds.join(',');
                }
            }
        }
    });
};

/**
 * Creates a new instance of ApplicationInsights.
 * @param {*} history - Application history object.
 * @param {*} env - App environment configuration.
 * @returns AppInsight's ReactPlugin instance.
 */
export const createAppInsights = (history, env) => {
    const { PORTAL_NAME, APPINSIGHTS } = env;
    const reactPlugin = new ReactPlugin();
    // this will be initialized by portalAuth redux slice
    reactPlugin.user = null;

    // See https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript for config documentation
    const appInsights = new ApplicationInsights({
        config: {
            extensions: [reactPlugin],
            extensionConfig: {
                [reactPlugin.identifier]: { history },
            },
            disableTelemetry: !APPINSIGHTS.ENABLED,
            instrumentationKey: APPINSIGHTS.INSTRUMENTATION_KEY,
        },
    });
    appInsights.loadAppInsights();

    usedInMemoryTabIdFallback = false;
    tabIdFallbackErrorName = null;

    // Generate unique tab identifier for disambiguating multi-tab scenarios
    const tabId = getOrCreateTabId();
    if (usedInMemoryTabIdFallback) {
        appInsights.trackTrace({
            message: 'AppInsights sessionStorage tab-id fallback activated',
            severityLevel: SeverityLevel.Warning,
            properties: {
                tabIdSource: 'in-memory',
                storageErrorName: tabIdFallbackErrorName || 'UnknownError',
            },
        });
    }

    // Configure telemetry and dependency tracking
    setupTelemetryInitializers(appInsights, reactPlugin, PORTAL_NAME, tabId);
    setupDependencyTracking(appInsights);

    // Copied from https://github.com/microsoft/ApplicationInsights-JS AppInsightsErrorBoundary.tsx
    // This mimics the way AppInsight's error boundary component reports exceptions and provides
    // a simplified way for calling code to report an exception.
    reactPlugin.handleException = function (error, errorInfo) {
        return this.trackException({
            error,
            exception: error,
            severityLevel: SeverityLevel.Error,
            properties: errorInfo,
        });
    };

    // Convenience methods to keep identity/context updates centralized
    reactPlugin.setAuthenticatedUser = (userId, accountId) => {
        try {
            if (userId) appInsights.setAuthenticatedUserContext(userId, accountId);
        } catch (_) {}
    };
    reactPlugin.clearAuthenticatedUser = () => {
        try {
            appInsights.clearAuthenticatedUserContext();
        } catch (_) {}
    };
    reactPlugin.setPatientContext = (ctx) => {
        reactPlugin.patientContext = ctx || {};
    };

    // assign global instance
    window.AppInsightsReactPlugin = reactPlugin;

    return reactPlugin;
};
